import { useEffect, useRef } from 'react';

/**
 * Shared ESC-to-close behaviour for all modals.
 *
 * Every modal used to register its own `window` keydown listener. That caused
 * two inconsistencies:
 *   1. Stacked modals (e.g. a form + a confirmation) all closed on a single
 *      ESC because every listener fired at once.
 *   2. A focused input/combobox could swallow the first ESC (bubbling phase),
 *      so the modal only closed on the second press.
 *
 * This module keeps a single LIFO stack of open modals and one document-level
 * listener registered in the capture phase. Only the top-most modal reacts, on
 * the first ESC press, and it honours `closeOnEsc` (a modal that opts out
 * blocks ESC entirely instead of letting a modal beneath it close).
 */
type EscEntry = { closeOnEsc: boolean; onEsc: () => void };

const escStack: EscEntry[] = [];
let listenerAttached = false;

function handleGlobalKeyDown(e: KeyboardEvent) {
  if (e.key !== 'Escape' && e.key !== 'Esc') return;
  const top = escStack[escStack.length - 1];
  if (!top) return;

  // Only the top-most modal reacts. If it opted out of ESC, do nothing — it
  // must not fall through and close a modal beneath it.
  if (!top.closeOnEsc) return;

  // Capture-phase stopPropagation guarantees a single ESC closes the modal even
  // when an input/combobox inside it is focused.
  e.stopPropagation();
  top.onEsc();
}

function attachListener() {
  if (listenerAttached) return;
  document.addEventListener('keydown', handleGlobalKeyDown, true);
  listenerAttached = true;
}

function detachListener() {
  if (!listenerAttached || escStack.length > 0) return;
  document.removeEventListener('keydown', handleGlobalKeyDown, true);
  listenerAttached = false;
}

/**
 * Registers a modal in the shared ESC stack while it is open.
 *
 * @param isOpen     Whether the modal is currently open/rendered.
 * @param onEsc      Called when ESC closes this (top-most) modal.
 * @param closeOnEsc When false, ESC is ignored for this modal.
 */
export function useModalEscClose(
  isOpen: boolean,
  onEsc: () => void,
  closeOnEsc = true
) {
  // Keep the latest callback/flag on a stable entry so we never re-subscribe.
  const entryRef = useRef<EscEntry>({ closeOnEsc, onEsc });
  entryRef.current.closeOnEsc = closeOnEsc;
  entryRef.current.onEsc = onEsc;

  useEffect(() => {
    if (!isOpen) return;
    const entry = entryRef.current;
    escStack.push(entry);
    attachListener();
    return () => {
      const idx = escStack.lastIndexOf(entry);
      if (idx !== -1) escStack.splice(idx, 1);
      detachListener();
    };
  }, [isOpen]);
}
